/*! *********************************************************************
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _clone from 'lodash/clone';

/**
 * Statuses of an Asset
 */
enum AssetStatus {
  PENDING = 'pending',
  INVALID = 'invalid',
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived'
}

/**
 * Types for an Asset
 */
enum AssetType {
  MATERIAL = 'substanceMaterial',
  SMART_MATERIAL = 'painterSmartMaterial',
  ATLAS = 'substanceAtlas',
  DECAL = 'substanceDecal',
  MODEL = 'model',
  SUBSTANCE_MODEL = 'substanceModel',
  FILTER = 'substanceFilter',
  TEXTURE_GENERATOR = 'substance2DGenerator',
  MESH_BASED_GENERATOR = 'substanceMeshBasedGenerator',
  IBL = 'substanceIbl'
}

/**
 * Kinds of a PreviewAttachment
 */
enum PreviewAttachmentKind {
  IMAGE = 'image',
  MODEL = 'model'
}

/**
 * Typenames for an Attachment
 */
enum AttachmentTypename {
  DOWNLOAD = 'DownloadAttachment',
  PREVIEW = 'PreviewAttachment'
}

/**
 * On client side we sometimes inject a file, a weight or a downloads property
 */
interface CustomBaseAttachment {
  file?: File;
  weight?: number;
  previewUrl?: string;
}

interface BaseAttachment extends CustomBaseAttachment {
  id: string;
  label: string;
  createdAt?: string;
  tags: string[];
}

interface DownloadAttachment extends BaseAttachment {
  __typename: AttachmentTypename.DOWNLOAD;
  filename: string;
  mimetype?: string;
  size: number;
  url: string;
  revisions: DownloadAttachmentRevision[];
  previewUrl?: string;
}

interface DownloadAttachmentRevision {
  filename: string;
  mimetype: string;
  size: number;
  createdAt: string;
  revision: number;
}

interface PreviewAttachment extends BaseAttachment {
  __typename: AttachmentTypename.PREVIEW;
  kind: PreviewAttachmentKind;
  url: string;
}

type AssetAttachment = DownloadAttachment | PreviewAttachment;

interface DownloadsInterface {
  [key: string]: DownloadAttachment;
}

interface AssetStats {
  downloads: number;
  likes: number;
}

interface AssetExtraData {
  key: string;
  value: string;
}

enum sizeUnit {
  meter = 'meter',
  centimeter = 'centimeter',
  millimeter = 'millimeter'
}

interface ComputedSizeInterface {
  unit: sizeUnit;
  value: number;
}

interface PhysicalSizesInterface {
  raw: number;
  computed: ComputedSizeInterface;
}

/**
 * Class used to describe an Asset from a graphql query
 */
class RawAsset {
  createdAt?: string;
  updatedAt?: string;
  id?: string;
  title?: string;
  description?: string;
  status?: AssetStatus;
  thumbnail?: PreviewAttachment;
  attachments?: AssetAttachment[];
  cost?: number;
  tags?: string[];
  suggestedTags?: string[];
  groups?: string[];
  category?: string;
  categories?: string[];
  type?: AssetType;
  stats?: AssetStats;
  authorId?: string;
  softwares?: string[];
  free?: boolean;
  new?: boolean;
  downloadsRecentlyUpdated?: boolean;
  downloadsUpdatedAt?: string;
  publishedAt?: string;
  extraData?: AssetExtraData[];
  collections?: object[];
  author?: object;

  constructor(rawAsset: Object) {
    if (rawAsset) Object.assign(this, _clone(rawAsset));
  }
}

export {
  AssetStatus,
  AssetType,
  PreviewAttachmentKind,
  AttachmentTypename,
  CustomBaseAttachment,
  BaseAttachment,
  DownloadAttachment,
  DownloadAttachmentRevision,
  PreviewAttachment,
  AssetAttachment,
  DownloadsInterface,
  AssetStats,
  AssetExtraData,
  sizeUnit,
  ComputedSizeInterface,
  PhysicalSizesInterface,
  RawAsset
};
